// Minimal stale-while-revalidate cache for API responses.
//
// Goal: open ANY asset/clients page instantly if its data was ever loaded
// before in this SPA session. Any mutation (create/update/delete) is
// responsible for calling `invalidate(key)` so the next read re-fetches.
//
// Not using react-query to avoid a new dep and keep bundle small.

const CACHE = new Map(); // key -> { data, ts, error, inflight: Promise|null }
const SUBS = new Map();  // key -> Set<fn(data)>

export function readCache(key) {
  const e = CACHE.get(key);
  return e ? e.data : undefined;
}

export function readCacheEntry(key) {
  return CACHE.get(key) || null;
}

export function subscribe(key, fn) {
  let set = SUBS.get(key);
  if (!set) { set = new Set(); SUBS.set(key, set); }
  set.add(fn);
  return () => { set.delete(fn); };
}

function notify(key, data) {
  const s = SUBS.get(key);
  if (!s) return;
  for (const fn of s) {
    try { fn(data); } catch (_e) { /* subscriber bug — swallow */ }
  }
}

export async function fetchAndCache(key, fetcher, { force = false } = {}) {
  const existing = CACHE.get(key);
  if (!force && existing?.inflight) return existing.inflight;
  const p = (async () => {
    try {
      const data = await fetcher();
      CACHE.set(key, { data, ts: Date.now(), error: null, inflight: null });
      notify(key, data);
      return data;
    } catch (err) {
      // Keep previous data visible on error; just surface the error upward.
      const prev = CACHE.get(key) || {};
      CACHE.set(key, { ...prev, error: err, inflight: null });
      throw err;
    }
  })();
  CACHE.set(key, { ...(existing || {}), inflight: p });
  return p;
}

export function invalidate(prefix) {
  // Prefix match so a single call can nuke a family of keys ("devices:*").
  for (const k of Array.from(CACHE.keys())) {
    if (k === prefix || k.startsWith(prefix + ":") || k.startsWith(prefix)) {
      CACHE.delete(k);
    }
  }
}

export function clearAll() {
  CACHE.clear();
  SUBS.clear();
}
